import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { logSupabaseError } from "./utils";
import "./App.css";
import { useMaximize } from "./hooks/useMaximize";

export default function StaffManager({ onClose, currentUser, embedded = false }) {
    const { toggle, overlayClass, panelClass, icon } = useMaximize(true);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("list");
    const [form, setForm] = useState({ email: "", password: "", full_name: "", role: "cashier" });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const successTimer = useRef(null);
    const [mobileRequests, setMobileRequests] = useState([]);
    const [mobileRequestsLoading, setMobileRequestsLoading] = useState(true);

    useEffect(() => {
        fetchStaff();
        fetchMobileRequests();
        return () => clearTimeout(successTimer.current);
    }, []);

    async function fetchStaff() {
        setLoading(true);
        const { data, error } = await supabase
            .from("profiles")
            .select("*")
            .order("created_at", { ascending: false });
        logSupabaseError("StaffManager: fetchStaff", error);
        if (data) setStaff(data);
        setLoading(false);
    }

    async function fetchMobileRequests() {
        setMobileRequestsLoading(true);
        const { data, error } = await supabase
            .from("mobile_access_requests")
            .select("id, cashier_id, requested_at, profiles!mobile_access_requests_cashier_id_fkey(full_name)")
            .eq("status", "pending")
            .order("requested_at", { ascending: true });
        logSupabaseError("StaffManager: fetchMobileRequests", error);
        if (data) setMobileRequests(data);
        setMobileRequestsLoading(false);
    }

    async function resolveMobileRequest(request, newStatus) {
        const { error } = await supabase
            .from("mobile_access_requests")
            .update({
                status: newStatus,
                resolved_at: new Date().toISOString(),
                resolved_by: currentUser?.id,
            })
            .eq("id", request.id);
        if (error) {
            alert("❌ Failed: " + error.message);
            return;
        }
        fetchMobileRequests();
    }

    function showSuccess(msg) {
        setSuccess(msg);
        clearTimeout(successTimer.current);
        successTimer.current = setTimeout(() => setSuccess(null), 5000);
    }

    async function handleCreateStaff() {
        if (!form.email || !form.password || !form.full_name) {
            setError("All fields are required!"); return;
        }
        if (form.password.length < 6) {
            setError("Password must be at least 6 characters!"); return;
        }

        setSaving(true);
        setError(null);

        try {
            const { data: { session } } = await supabase.auth.getSession();

            if (!session) {
                setError("You must be logged in to create staff accounts.");
                setSaving(false);
                return;
            }

            const res = await fetch(
                `https://whddduomeokqsvovnicn.supabase.co/functions/v1/create-cashier`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        email: form.email.trim().toLowerCase(),
                        password: form.password,
                        full_name: form.full_name,
                        phone: "",
                    }),
                }
            );

            const result = await res.json();

            if (!res.ok || result.ok === false) {
                setError(result.error || "Failed to create account.");
                setSaving(false);
                return;
            }

            showSuccess(`✅ Account created for ${form.full_name}!\nThey can log in with: ${form.email}`);
            setForm({ email: "", password: "", full_name: "", role: "cashier" });
            fetchStaff();
        } catch (err) {
            setError("Unexpected error: " + err.message);
        }

        setSaving(false);
    }

    // NEW
    async function toggleActive(member) {
        if (member.id === currentUser?.id) {
            alert("You cannot deactivate your own account!"); return;
        }
        if (member.is_owner) {
            alert("This account is protected and cannot be deactivated."); return;
        }
        const newRole = member.role === "disabled" ? "cashier" : "disabled";
        const { error } = await supabase
            .from("profiles")
            .update({ role: newRole })
            .eq("id", member.id);
        if (error) {
            alert("❌ Failed: " + error.message);
            return;
        }
        fetchStaff();
    }
    // NEW handleDelete
    async function handleDelete(member) {
        if (member.id === currentUser?.id) {
            alert("You cannot delete your own account!"); return;
        }
        if (member.is_owner) {
            alert("This account is protected and cannot be deleted."); return;
        }
        if (!window.confirm(`Delete "${member.full_name}"?\nThis cannot be undone.`)) return;

        const { error } = await supabase.rpc("delete_staff_account", {
            p_user_id: member.id,
        });

        if (error) {
            if (error.message.includes("transaction history")) {
                alert(`❌ Cannot delete ${member.full_name}.\n\nThis account has sales transaction history which must be preserved for reporting.\n\nUse the ⊘ button to deactivate the account instead.`);
            } else {
                alert("❌ Delete failed: " + error.message);
            }
            return;
        }
        fetchStaff();
    }

    // eslint-disable-next-line no-unused-vars
    async function handlePasswordReset(member) {
        if (!window.confirm(`Send password reset email to ${member.full_name}?`)) return;
        const { error } = await supabase.auth.resetPasswordForEmail(
            member.email ?? "",
            { redirectTo: window.location.origin }
        );
        if (error) alert("Error: " + error.message);
        else alert(`✅ Password reset email sent to ${member.full_name}`);
    }

    const roleColors = {
        manager: { color: "var(--cyan)", bg: "var(--cyan-dim)" },
        cashier: { color: "var(--amber)", bg: "var(--amber-dim)" },
        disabled: { color: "var(--red)", bg: "var(--red-dim)" },
    };

    // ── Shared inner content (used in both embedded and standalone) ────────────
    const inner = (
        <>
            {/* Sub-tabs */}
            <div className="report-header" style={embedded ? { padding: "12px 16px" } : {}}>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                    <div className="inv-tabs">
                        <button className={`inv-tab ${tab === "list" ? "active" : ""}`} onClick={() => setTab("list")}>
                            👥 Staff List
                        </button>
                        <button className={`inv-tab ${tab === "create" ? "active" : ""}`} onClick={() => setTab("create")}>
                            ➕ Add Staff
                        </button>
                        <button className={`inv-tab ${tab === "requests" ? "active" : ""}`} onClick={() => setTab("requests")}>
                            📱 Mobile Requests
                            {mobileRequests.length > 0 && (
                                <span style={{
                                    marginLeft: "6px", padding: "1px 6px", borderRadius: "20px",
                                    fontSize: "10px", fontWeight: 700,
                                    background: "var(--red-dim)", color: "var(--red)",
                                }}>
                                    {mobileRequests.length}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
                {!embedded && (
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button className="maximize-btn" onClick={toggle} title={icon === "⊞" ? "Maximize" : "Restore"}>
                            {icon}
                        </button>
                    </div>
                )}
            </div>

            {/* ── Staff List Tab ── */}
            {tab === "list" && (
                <div className="report-table-wrap">
                    {loading ? (
                        <p className="report-loading">Loading...</p>
                    ) : staff.length === 0 ? (
                        <div className="report-empty">
                            <p>👥</p>
                            <p>No staff accounts yet</p>
                        </div>
                    ) : (
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Staff Member</th>
                                    <th>Role</th>
                                    <th>Since</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {staff.map((member) => {
                                    const roleStyle = roleColors[member.role] ?? roleColors.cashier;
                                    const isMe = member.id === currentUser?.id;
                                    return (
                                        <tr key={member.id} style={{ opacity: member.role === "disabled" ? 0.6 : 1 }}>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                                    <div style={{
                                                        width: "36px", height: "36px", borderRadius: "50%",
                                                        background: roleStyle.bg, border: `1px solid ${roleStyle.color}40`,
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        fontSize: "15px", fontWeight: 700, color: roleStyle.color, flexShrink: 0,
                                                    }}>
                                                        {member.full_name?.charAt(0)?.toUpperCase() ?? "?"}
                                                    </div>
                                                    <div>
                                                        <p style={{ fontWeight: 600, color: "var(--text)", fontSize: "13px", margin: 0 }}>
                                                            {member.full_name}
                                                            {isMe && (
                                                                <span style={{ fontSize: "10px", color: "var(--text3)", marginLeft: "6px" }}>(you)</span>
                                                            )}
                                                            {member.role === "disabled" && (
                                                                <span style={{ fontSize: "10px", color: "var(--red)", marginLeft: "6px" }}>· disabled</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span style={{
                                                    padding: "3px 10px", borderRadius: "20px",
                                                    fontSize: "11px", fontWeight: 700,
                                                    textTransform: "uppercase", letterSpacing: "0.5px",
                                                    background: roleStyle.bg, color: roleStyle.color,
                                                }}>
                                                    {member.role}
                                                </span>
                                            </td>
                                            <td className="time-col">
                                                {new Date(member.created_at).toLocaleDateString("en-US", {
                                                    month: "short", day: "numeric", year: "numeric",
                                                })}
                                            </td>
                                            <td>
                                                <div className="action-btns">
                                                    {!isMe && (
                                                        <button
                                                            className="edit-btn"
                                                            title={member.role === "disabled" ? "Activate account" : "Deactivate account"}
                                                            onClick={() => toggleActive(member)}
                                                            style={member.role === "disabled"
                                                                ? { borderColor: "var(--green)", color: "var(--green)" }
                                                                : { borderColor: "var(--amber)", color: "var(--amber)" }}
                                                        >
                                                            {member.role === "disabled" ? "✓" : "⊘"}
                                                        </button>
                                                    )}
                                                    {!isMe && (
                                                        <button
                                                            className="delete-btn"
                                                            title="Delete account"
                                                            onClick={() => handleDelete(member)}
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Create Staff Tab ── */}
            {tab === "create" && (
                <div className="wh-form-wrap">
                    <div className="wh-section">
                        <p className="wh-section-title">➕ Create New Staff Account</p>

                        {error && (
                            <div style={{
                                padding: "10px 14px", background: "var(--red-dim)",
                                border: "1px solid rgba(255,69,58,0.3)", borderRadius: "var(--radius)",
                                color: "var(--red)", fontSize: "13px", marginBottom: "14px",
                            }}>
                                ⚠ {error}
                                <button onClick={() => setError(null)} style={{ float: "right", background: "none", border: "none", color: "var(--red)", cursor: "pointer" }}>✕</button>
                            </div>
                        )}

                        {success && (
                            <div style={{
                                padding: "10px 14px", background: "var(--green-dim)",
                                border: "1px solid rgba(50,215,75,0.3)", borderRadius: "var(--radius)",
                                color: "var(--green)", fontSize: "13px", marginBottom: "14px",
                                whiteSpace: "pre-line",
                            }}>
                                {success}
                            </div>
                        )}

                        <div className="form-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <div className="form-field" style={{ gridColumn: "span 2" }}>
                                <label>Full Name *</label>
                                <input
                                    value={form.full_name}
                                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                                    placeholder="e.g. Tigist Haile"
                                />
                            </div>
                            <div className="form-field">
                                <label>Email Address *</label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    placeholder="e.g. tigist@guadaye.com"
                                />
                            </div>
                            <div className="form-field">
                                <label>Role *</label>
                                <select
                                    value={form.role}
                                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                                    style={{
                                        height: "36px", padding: "0 12px",
                                        background: "var(--elevated)", border: "1px solid var(--border)",
                                        borderRadius: "var(--radius)", color: "var(--text)",
                                        fontFamily: "var(--font)", outline: "none", width: "100%",
                                    }}
                                >
                                    <option value="cashier">🧾 Cashier</option>
                                    <option value="manager">👔 Manager</option>
                                </select>
                            </div>
                            <div className="form-field" style={{ gridColumn: "span 2" }}>
                                <label>Temporary Password *</label>
                                <input
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    placeholder="Min. 6 characters"
                                />
                                <p style={{ fontSize: "11px", color: "var(--text3)", marginTop: "4px" }}>
                                    💡 Staff can change this after logging in via their Profile
                                </p>
                            </div>
                        </div>

                        <button
                            className="save-btn"
                            onClick={handleCreateStaff}
                            disabled={saving}
                            style={{ marginTop: "16px" }}
                        >
                            {saving ? "Creating..." : "➕ Create Account"}
                        </button>
                    </div>

                    {/* Role permissions */}
                    <div className="wh-section">
                        <p className="wh-section-title">ℹ️ Role Permissions</p>
                        <div className="staff-role-perms-row">
                            <div style={{
                                padding: "14px", background: "var(--cyan-dim)",
                                border: "1px solid rgba(50,212,224,0.2)", borderRadius: "var(--radius)",
                            }}>
                                <p style={{ color: "var(--cyan)", fontWeight: 700, fontSize: "13px", marginBottom: "10px" }}>👔 Manager</p>
                                <ul style={{ color: "var(--text2)", fontSize: "12px", paddingLeft: "16px", display: "flex", flexDirection: "column", gap: "5px" }}>
                                    <li>Full POS access</li>
                                    <li>Products & Categories</li>
                                    <li>Warehouse & Inventory</li>
                                    <li>Sales Reports & Analytics</li>
                                    <li>Staff Management</li>
                                    <li>Store Settings</li>
                                </ul>
                            </div>
                            <div style={{
                                padding: "14px", background: "var(--amber-dim)",
                                border: "1px solid rgba(255,179,64,0.2)", borderRadius: "var(--radius)",
                            }}>
                                <p style={{ color: "var(--amber)", fontWeight: 700, fontSize: "13px", marginBottom: "10px" }}>🧾 Cashier</p>
                                <ul style={{ color: "var(--text2)", fontSize: "12px", paddingLeft: "16px", display: "flex", flexDirection: "column", gap: "5px" }}>
                                    <li>POS sales only</li>
                                    <li>View products</li>
                                    <li>Process checkouts</li>
                                    <li>No reports access</li>
                                    <li>No management access</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Mobile Access Requests Tab ── */}
            {tab === "requests" && (
                <div className="report-table-wrap">
                    {mobileRequestsLoading ? (
                        <p className="report-loading">Loading...</p>
                    ) : mobileRequests.length === 0 ? (
                        <div className="report-empty">
                            <p>📱</p>
                            <p>No pending mobile access requests</p>
                        </div>
                    ) : (
                        <table className="report-table">
                            <thead>
                                <tr>
                                    <th>Cashier</th>
                                    <th>Requested</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mobileRequests.map((request) => (
                                    <tr key={request.id}>
                                        <td style={{ fontWeight: 600, color: "var(--text)" }}>
                                            {request.profiles?.full_name ?? "Unknown"}
                                        </td>
                                        <td className="time-col">
                                            {new Date(request.requested_at).toLocaleString("en-US", {
                                                month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                                            })}
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <button
                                                    className="edit-btn"
                                                    title="Approve access"
                                                    onClick={() => resolveMobileRequest(request, "approved")}
                                                    style={{ width: "auto", padding: "0 10px", borderColor: "var(--green)", color: "var(--green)" }}
                                                >
                                                    ✓ Approve
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    title="Deny access"
                                                    onClick={() => resolveMobileRequest(request, "denied")}
                                                    style={{ width: "auto", padding: "0 10px" }}
                                                >
                                                    ✕ Deny
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </>
    );

    // ── Standalone mode (not embedded) ────────────────────────────────────────
    if (!embedded) {
        return (
            <div className={overlayClass}>
                <div className={panelClass} style={{ maxWidth: "820px" }}>
                    <div className="report-header">
                        <div>
                            <h2 className="report-title">👥 Staff Manager</h2>
                            <p className="report-date">{staff.length} account{staff.length !== 1 ? "s" : ""}</p>
                        </div>
                        <button className="report-close" onClick={onClose}>✕</button>
                    </div>
                    {inner}
                </div>
            </div>
        );
    }

    // ── Embedded mode (inside Settings) ───────────────────────────────────────
    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            {inner}
        </div>
    );
}